import {
  init,
  launchModal,
  launchPaymentModal,
  disconnect as bitcoinConnectDisconnect,
  onConnected,
  onDisconnected,
} from '@getalby/bitcoin-connect-react';
import type { WebLNProvider } from '@webbtc/webln-types';

interface LnurlPayInfo {
  callback: string;
  maxSendable: number;
  minSendable: number;
  tag: string;
  commentAllowed?: number;
}

export interface ZapPayment {
  invoice: string;
  preimage: string;
}

export interface WalletInfo {
  node?: { alias?: string };
}

export interface WalletStatus {
  connected: boolean;
  walletInfo: WalletInfo | null;
}

const DISCONNECTED_STATUS: WalletStatus = { connected: false, walletInfo: null };

class LightningService {
  provider: WebLNProvider | null = null;
  private initialized = false;
  private status: WalletStatus = DISCONNECTED_STATUS;
  private readonly listeners = new Set<() => void>();

  private setStatus(status: WalletStatus) {
    this.status = status;
    this.listeners.forEach((listener) => {
      listener();
    });
  }

  /** For use with `useSyncExternalStore`. */
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): WalletStatus => this.status;

  private ensureInit() {
    if (this.initialized) return;
    this.initialized = true;

    init({ appName: document.title, showBalance: false });

    onConnected((provider) => {
      this.provider = provider;
      this.setStatus({ connected: true, walletInfo: null });

      provider
        .getInfo?.()
        .then((info) => {
          this.setStatus({ connected: true, walletInfo: info });
        })
        .catch(() => {
          // Not all wallets support getInfo(); connection still succeeded.
        });
    });

    onDisconnected(() => {
      this.provider = null;
      this.setStatus(DISCONNECTED_STATUS);
    });
  }

  /** Opens the bitcoin-connect wallet-picker (NWC pairing, WebLN extensions, etc.) on its own, with no invoice to pay. */
  connectWallet() {
    this.ensureInit();
    launchModal();
  }

  disconnectWallet() {
    bitcoinConnectDisconnect();
  }

  async fetchInvoice(
    address: string,
    sats: number,
    comment?: string,
  ): Promise<{ invoice: string; verify?: string }> {
    this.ensureInit();

    const [user, domain] = address.split('@');
    if (!user || !domain) throw new Error('Invalid lightning address');

    const res = await fetch(`https://${domain}/.well-known/lnurlp/${user}`);
    if (!res.ok) throw new Error('Could not reach Lightning provider');
    const info = (await res.json()) as LnurlPayInfo;
    if (info.tag !== 'payRequest')
      throw new Error('Not a valid LNURL-pay address');

    const msats = sats * 1000;
    if (msats < info.minSendable || msats > info.maxSendable) {
      throw new Error(
        `Amount must be between ${info.minSendable / 1000} and ${info.maxSendable / 1000} sats`,
      );
    }

    const url = new URL(info.callback);
    url.searchParams.set('amount', String(msats));
    if (comment && info.commentAllowed && comment.length <= info.commentAllowed) {
      url.searchParams.set('comment', comment);
    }

    const res2 = await fetch(url.toString());
    if (!res2.ok) throw new Error('Failed to generate invoice');
    const data = (await res2.json()) as {
      pr?: string;
      reason?: string;
      verify?: string;
    };
    if (data.reason) throw new Error(data.reason);
    if (!data.pr) throw new Error('No invoice returned');
    return { invoice: data.pr, verify: data.verify };
  }

  payInvoice(invoice: string, verify?: string): Promise<ZapPayment | null> {
    this.ensureInit();

    if (this.provider) {
      return this.provider
        .sendPayment(invoice)
        .then((result) => ({ preimage: result.preimage, invoice }));
    }

    return new Promise((resolve) => {
      let settled = false;

      const { setPaid } = launchPaymentModal({
        invoice,
        onPaid: (response) => {
          settled = true;
          resolve({ preimage: response.preimage, invoice });
        },
        onCancelled: () => {
          settled = true;
          resolve(null);
        },
      });

      // Bitcoin Connect's "Scan to Pay" QR flow has no way to know a payment
      // made from an external wallet has settled unless we tell it. LUD-21
      // gives us a `verify` URL to poll for that ourselves when the provider
      // supports it; `setPaid` feeds the result back into the modal's UI.
      if (verify) {
        void this.pollLnurlVerify(verify, () => settled, (preimage) => {
          settled = true;
          setPaid({ preimage });
          resolve({ preimage, invoice });
        });
      }
    });
  }

  private async pollLnurlVerify(
    verify: string,
    isSettled: () => boolean,
    onSettled: (preimage: string) => void,
  ) {
    const POLL_INTERVAL_MS = 2500;
    const MAX_ATTEMPTS = 240; // ~10 minutes

    for (let attempt = 0; attempt < MAX_ATTEMPTS && !isSettled(); attempt++) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (isSettled()) return;

      try {
        const res = await fetch(verify);
        if (!res.ok) continue;
        const data = (await res.json()) as {
          settled?: boolean;
          preimage?: string;
        };
        if (data.settled && data.preimage) {
          onSettled(data.preimage);
          return;
        }
      } catch {
        // Transient network error; keep polling until MAX_ATTEMPTS.
      }
    }
  }

  async payToLightningAddress(
    address: string,
    sats: number,
    comment?: string,
  ): Promise<ZapPayment | null> {
    const { invoice, verify } = await this.fetchInvoice(address, sats, comment);
    return this.payInvoice(invoice, verify);
  }
}

const lightningService = new LightningService();

export default lightningService;
